import type {
  VocabWord,
  VocabWordTranslated,
  UserVocabWithWord,
  VocabReviewResult,
  VocabRevertSrsPayload,
  TranscriptExtractResult,
  EnrichWordResult,
  CreateVocabWordsPayload,
  UpdateVocabWordPayload,
  UpdateUserVocabOverridesPayload,
  BulkImportWordsPayload,
  BulkImportWordsResponse,
} from "@nicsmith/shared_types";
import { baseApi } from "./api";

/** Paginated response from GET /vocab/my */
export interface PaginatedUserVocabResponse {
  items: UserVocabWithWord[];
  total: number;
  totalUnfiltered: number;
  page: number;
  pageSize: number;
  totalPages: number;
  cursor: string | null;
}

/** Query params for server-side filtered/sorted vocab */
export interface UserVocabQueryParams {
  page?: number;
  pageSize?: number;
  lang?: string;
  search?: string;
  srsState?: string;    // comma-separated: new,learning,review,relearning,mastered,due
  cefrLevel?: string;   // comma-separated: A1,A2,B1,B2,C1,C2
  wordType?: string;    // comma-separated: noun,verb,adjective,...
  sortField?: string;   // german, added_at, next_review_at, cefr_level, word_type, status
  sortDir?: string;     // asc, desc
}

/** Lightweight SRS stats from GET /vocab/my/stats */
export interface UserVocabStats {
  total: number;
  due: number;
  mastered: number;
  learning: number;
  totalReviews: number;
  correctReviews: number;
  retentionRate: number;
  newCount: number;
  learningCount: number;
  reviewCount: number;
  relearningCount: number;
  moduleIds: number[];
}

export const vocabApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get all words for a module (translated to user's language)
    getModuleVocab: builder.query<VocabWordTranslated[], number | { moduleId: number; lang?: string }>({
      query: (arg) => {
        const moduleId = typeof arg === "number" ? arg : arg.moduleId;
        const lang = typeof arg === "number" ? undefined : arg.lang;
        return lang
          ? `/vocab/modules/${moduleId}?lang=${lang}`
          : `/vocab/modules/${moduleId}`;
      },
      providesTags: (_result, _error, arg) => {
        const moduleId = typeof arg === "number" ? arg : arg.moduleId;
        return [{ type: "Vocab", id: `module-${moduleId}` }, "Vocab"];
      },
    }),

    // Get user's personal vocab set with SRS state (server-side filtered/sorted/paginated)
    getUserVocab: builder.query<
      PaginatedUserVocabResponse,
      UserVocabQueryParams | void
    >({
      query: (params) => {
        const qs = new URLSearchParams();
        if (params) {
          // Use != null checks instead of truthiness to avoid dropping 0 / empty-string values
          if (params.page != null) qs.set("page", String(params.page));
          if (params.pageSize != null) qs.set("pageSize", String(params.pageSize));
          if (params.lang) qs.set("lang", params.lang);
          if (params.search) qs.set("search", params.search);
          if (params.srsState) qs.set("srsState", params.srsState);
          if (params.cefrLevel) qs.set("cefrLevel", params.cefrLevel);
          if (params.wordType) qs.set("wordType", params.wordType);
          if (params.sortField) qs.set("sortField", params.sortField);
          if (params.sortDir) qs.set("sortDir", params.sortDir);
        }
        return `/vocab/my?${qs.toString()}`;
      },
      // Each unique combination of filters/sort/page is a separate cache entry
      serializeQueryArgs: ({ queryArgs }) => {
        return JSON.stringify(queryArgs ?? {});
      },
      providesTags: ["UserVocab"],
    }),

    // Get user vocab SRS stats (lightweight — no word details)
    getUserVocabStats: builder.query<UserVocabStats, void>({
      query: () => "/vocab/my/stats",
      providesTags: ["UserVocab"],
    }),

    // Get words due for review
    getDueVocab: builder.query<UserVocabWithWord[], { limit?: number; lang?: string } | void>({
      query: (params) => {
        const limit = params && "limit" in params ? params.limit : 20;
        const lang = params && "lang" in params ? params.lang : undefined;
        const qs = lang ? `limit=${limit}&lang=${lang}` : `limit=${limit}`;
        return `/vocab/my/due?${qs}`;
      },
      providesTags: ["VocabDue"],
    }),

    // AI extract vocabulary from transcript (admin)
    extractFromTranscript: builder.mutation<
      TranscriptExtractResult,
      { transcript: string; module_id: number }
    >({
      query: (body) => ({
        url: "/vocab/extract",
        method: "POST",
        body,
      }),
    }),

    // Bulk create words after admin review
    bulkCreateWords: builder.mutation<
      { words: VocabWord[]; count: number },
      { moduleId: number; words: CreateVocabWordsPayload["words"] }
    >({
      query: ({ moduleId, words }) => ({
        url: `/vocab/modules/${moduleId}/words`,
        method: "POST",
        body: { words },
      }),
      invalidatesTags: (_result, _error, { moduleId }) => [
        { type: "Vocab", id: `module-${moduleId}` },
        "Vocab",
        "AllVocabWords",
      ],
    }),

    // Update a single word (admin)
    updateVocabWord: builder.mutation<
      VocabWord,
      { id: number; data: UpdateVocabWordPayload }
    >({
      query: ({ id, data }) => ({
        url: `/vocab/words/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["Vocab", "AllVocabWords"],
    }),

    // Delete a single word (admin)
    deleteVocabWord: builder.mutation<void, number>({
      query: (id) => ({
        url: `/vocab/words/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Vocab", "AllVocabWords"],
    }),

    // Add word to personal vocab set
    addToMyVocab: builder.mutation<any, { wordId: number; source?: "module" | "exercise" }>({
      query: ({ wordId, source }) => ({
        url: `/vocab/my/${wordId}`,
        method: "POST",
        body: { source: source || "module" },
      }),
      invalidatesTags: ["UserVocab", "Vocab", "VocabDue"],
    }),

    // Remove word from personal vocab set
    removeFromMyVocab: builder.mutation<void, number>({
      query: (wordId) => ({
        url: `/vocab/my/${wordId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["UserVocab", "Vocab", "VocabDue"],
    }),

    // Submit review result (FSRS rating: 1=Again, 2=Hard, 3=Good, 4=Easy)
    reviewWord: builder.mutation<
      VocabReviewResult,
      { wordId: number; rating: number }
    >({
      query: ({ wordId, rating }) => ({
        url: `/vocab/my/${wordId}/review`,
        method: "POST",
        body: { rating },
      }),
      invalidatesTags: ["UserVocab", "VocabDue"],
    }),

    // Revert SRS state (undo last review)
    revertSrs: builder.mutation<
      VocabReviewResult,
      { wordId: number; payload: VocabRevertSrsPayload }
    >({
      query: ({ wordId, payload }) => ({
        url: `/vocab/my/${wordId}/revert-srs`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["UserVocab", "VocabDue"],
    }),

    // AI-enrich a word from exercise context
    enrichWord: builder.mutation<
      EnrichWordResult,
      { word: string; context?: string }
    >({
      query: (body) => ({
        url: "/vocab/enrich-word",
        method: "POST",
        body,
      }),
    }),

    // Add a custom word with AI enrichment + TTS
    addCustomWord: builder.mutation<
      any,
      { word: string; context?: string; context_sentence?: string; context_translation?: string;
        exercise_id?: number; module_id?: number; course_id?: number;
        hint_cefr_level?: string; hint_category?: string; hint_part_of_speech?: string;
        hint_gender?: string | null; hint_plural?: string | null;
        hint_present_er?: string | null; hint_prateritum?: string | null; hint_perfekt?: string | null;
        hint_translation?: string; hint_word_base_form?: string }
    >({
      query: (body) => ({
        url: "/vocab/my/add-word",
        method: "POST",
        body,
      }),
      invalidatesTags: ["UserVocab", "VocabDue"],
    }),

    // Check if a word already exists in user's personal vocab
    checkDuplicate: builder.mutation<
      { exists: boolean; existingWord?: VocabWordTranslated },
      { german: string }
    >({
      query: (body) => ({
        url: "/vocab/check-duplicate",
        method: "POST",
        body,
      }),
    }),

    // Save a pre-enriched custom word (skip AI, user already previewed)
    saveEnrichedWord: builder.mutation<any, EnrichWordResult>({
      query: (body) => ({
        url: "/vocab/my/save-enriched-word",
        method: "POST",
        body,
      }),
      invalidatesTags: ["UserVocab", "VocabDue"],
    }),

    // Bulk-import many pre-enriched words. Server-side dedup + summary counts.
    // Single round-trip replaces N saveEnrichedWord calls for the list-import flow.
    bulkImportEnrichedWords: builder.mutation<BulkImportWordsResponse, BulkImportWordsPayload>({
      query: (body) => ({
        url: "/vocab/my/bulk-import",
        method: "POST",
        body,
      }),
      invalidatesTags: ["UserVocab", "VocabDue"],
    }),

    // Batch generate TTS audio for all words in a module missing audio
    generateModuleAudio: builder.mutation<
      { generated: number; skipped: number; failed: number },
      { moduleId: number; regenerate?: boolean }
    >({
      query: ({ moduleId, regenerate }) => ({
        url: `/vocab/modules/${moduleId}/generate-audio${regenerate ? "?regenerate=true" : ""}`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { moduleId }) => [
        { type: "Vocab", id: `module-${moduleId}` },
        "Vocab",
      ],
    }),

    // Update personal overrides on a word (learner) or global word (custom)
    updateUserVocabWord: builder.mutation<
      any,
      { wordId: number; data: UpdateUserVocabOverridesPayload }
    >({
      query: ({ wordId, data }) => ({
        url: `/vocab/my/${wordId}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["UserVocab", "VocabDue", "Vocab"],
    }),

    // Generate TTS audio for a word
    generateAudio: builder.mutation<
      { id: number; audio_url: string },
      number
    >({
      query: (id) => ({
        url: `/vocab/words/${id}/generate-audio`,
        method: "POST",
      }),
      invalidatesTags: ["Vocab", "UserVocab"],
    }),
  }),
  overrideExisting: true,
});

export const {
  useGetModuleVocabQuery,
  useGetUserVocabQuery,
  useGetUserVocabStatsQuery,
  useGetDueVocabQuery,
  useExtractFromTranscriptMutation,
  useBulkCreateWordsMutation,
  useUpdateVocabWordMutation,
  useDeleteVocabWordMutation,
  useAddToMyVocabMutation,
  useRemoveFromMyVocabMutation,
  useReviewWordMutation,
  useRevertSrsMutation,
  useEnrichWordMutation,
  useAddCustomWordMutation,
  useCheckDuplicateMutation,
  useSaveEnrichedWordMutation,
  useBulkImportEnrichedWordsMutation,
  useUpdateUserVocabWordMutation,
  useGenerateAudioMutation,
  useGenerateModuleAudioMutation,
} = vocabApi;
